import coreURL from "@ffmpeg/core?url";
import wasmURL from "@ffmpeg/core/wasm?url";
import { useCallback, useState } from "react";
import { runExclusiveFfmpeg } from "@/lib/hooks/ffmpeg-lock";

// ?url imports (vs CDN + toBlobURL): @ffmpeg/ffmpeg's module worker can
// only load the core via dynamic ESM import. The toBlobURL pattern from
// older guides points at a UMD blob that has no default ESM export →
// silent ERROR_IMPORT_FAILURE. ?url emits same-origin static assets the
// module worker can dynamic-import directly.

interface UseAudioTranscoderApi {
  transcoding: boolean;
  transcodeToM4a: (input: Blob) => Promise<Blob>;
  transcodeToMp3: (input: Blob) => Promise<Blob>;
}

let ffmpegInstancePromise: Promise<{
  ffmpeg: import("@ffmpeg/ffmpeg").FFmpeg;
}> | null = null;

const loadFFmpeg = async () => {
  if (ffmpegInstancePromise) return ffmpegInstancePromise;

  ffmpegInstancePromise = (async () => {
    const { FFmpeg } = await import("@ffmpeg/ffmpeg");
    const ffmpeg = new FFmpeg();
    await ffmpeg.load({ coreURL, wasmURL });
    return { ffmpeg };
  })();

  try {
    return await ffmpegInstancePromise;
  } catch (err) {
    ffmpegInstancePromise = null;
    throw err;
  }
};

const inferExtension = (mimeType: string): string => {
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("ogg")) return "ogg";
  return "bin";
};

export const useAudioTranscoder = (): UseAudioTranscoderApi => {
  const [transcoding, setTranscoding] = useState(false);

  const runExclusive = useCallback(
    (work: () => Promise<Blob>) =>
      runExclusiveFfmpeg(async () => {
        setTranscoding(true);
        try {
          return await work();
        } finally {
          setTranscoding(false);
        }
      }),
    [],
  );

  const transcodeToM4a = useCallback(
    async (input: Blob): Promise<Blob> =>
      runExclusive(async () => {
        const { ffmpeg } = await loadFFmpeg();
        const ext = inferExtension(input.type);
        const inputName = `input.${ext}`;
        const outputName = "output.m4a";

        const inputBytes = new Uint8Array(await input.arrayBuffer());
        await ffmpeg.writeFile(inputName, inputBytes);

        // 128k AAC + faststart for streaming playback before full download.
        await ffmpeg.exec([
          "-i",
          inputName,
          "-c:a",
          "aac",
          "-b:a",
          "128k",
          "-movflags",
          "+faststart",
          outputName,
        ]);

        const data = await ffmpeg.readFile(outputName);
        await ffmpeg.deleteFile(inputName);
        await ffmpeg.deleteFile(outputName);
        if (typeof data === "string") {
          throw new Error("ffmpeg.readFile returned string, expected bytes");
        }
        const bytes = new Uint8Array(data);

        return new Blob([bytes], { type: "audio/mp4" });
      }),
    [runExclusive],
  );

  const transcodeToMp3 = useCallback(
    async (input: Blob): Promise<Blob> =>
      runExclusive(async () => {
        const { ffmpeg } = await loadFFmpeg();
        const ext = inferExtension(input.type);
        const inputName = `input.${ext}`;
        const outputName = "output.mp3";

        const inputBytes = new Uint8Array(await input.arrayBuffer());
        await ffmpeg.writeFile(inputName, inputBytes);

        await ffmpeg.exec([
          "-i",
          inputName,
          "-c:a",
          "libmp3lame",
          "-b:a",
          "128k",
          outputName,
        ]);

        const data = await ffmpeg.readFile(outputName);
        await ffmpeg.deleteFile(inputName);
        await ffmpeg.deleteFile(outputName);
        if (typeof data === "string") {
          throw new Error("ffmpeg.readFile returned string, expected bytes");
        }
        const bytes = new Uint8Array(data);

        return new Blob([bytes], { type: "audio/mpeg" });
      }),
    [runExclusive],
  );

  return { transcoding, transcodeToM4a, transcodeToMp3 };
};
